import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Loader2, ChevronDown, ChevronUp, RotateCcw, CheckCircle2, AlertCircle, Lightbulb, Copy, Check, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useFeatureAccess } from '../context/FeatureAccessContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const tb = { fontFamily: "'Tiro Bangla', serif" };

const CATEGORY_COLORS = {
    correctness: { bg: 'bg-red-500', light: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', ring: 'ring-red-500' },
    clarity: { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500' },
    engagement: { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-500' },
    delivery: { bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', ring: 'ring-violet-500' },
};

const CircularScore = ({ score, size = 56 }) => {
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const color = score >= 80 ? '#4CAF50' : score >= 50 ? '#F59E0B' : '#EF4444';

    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg className="transform -rotate-90" width={size} height={size}>
                <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E5E7EB" strokeWidth="4" fill="none" />
                <circle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth="4" fill="none"
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                    className="transition-all duration-700 ease-out" />
            </svg>
            <span className="absolute text-sm font-bold" style={{ color }}>{score}</span>
        </div>
    );
};

const CategoryBar = ({ name, data }) => {
    const colors = CATEGORY_COLORS[name] || CATEGORY_COLORS.correctness;
    return (
        <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-slate-600 w-14 truncate font-bengali">{data.label_bn}</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ease-out ${colors.bg}`}
                    style={{ width: `${data.score}%` }} />
            </div>
            <span className="text-[11px] font-bold text-slate-700 w-7 text-right">{data.score}</span>
        </div>
    );
};

const ErrorItem = ({ error, index }) => {
    const typeColor = {
        'Grammar': 'bg-red-100 text-red-700',
        'Spelling': 'bg-orange-100 text-orange-700',
        'Punctuation': 'bg-amber-100 text-amber-700',
        'Clarity': 'bg-blue-100 text-blue-700',
        'Style': 'bg-violet-100 text-violet-700',
    };
    const color = typeColor[error.type] || 'bg-slate-100 text-slate-700';
    return (
        <div className="p-2.5 bg-white rounded-lg border border-slate-100 space-y-1" data-testid={`grammar-error-${index}`}>
            <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${color}`}>{error.type}</span>
            </div>
            {error.wrong_text && (
                <div className="flex items-start gap-1.5 text-[11px]">
                    <span className="text-red-500 line-through font-mono">{error.wrong_text}</span>
                    <span className="text-slate-400">&rarr;</span>
                    <span className="text-green-600 font-mono font-medium">{error.suggestion || ''}</span>
                </div>
            )}
            <p className="text-[11px] text-slate-500 font-bengali">{error.message_bn || error.message || ''}</p>
        </div>
    );
};

export default function FloatingGrammarly() {
    const { isLocked } = useFeatureAccess();
    const { user, isAuthenticated } = useAuth();
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('result');
    const [chatStatus, setChatStatus] = useState(null);
    const [limitReached, setLimitReached] = useState(false);
    const panelRef = useRef(null);

    const fetchChatStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const res = await fetch(`${API_URL}/api/chatbot/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setChatStatus(data);
            setLimitReached(data.remaining <= 0);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        if (isAuthenticated && user?.role === 'student') fetchChatStatus();
    }, [isAuthenticated, user]);

    useEffect(() => {
        const handleClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-testid="grammarly-fab"]')) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const checkGrammar = async () => {
        if (!text.trim() || checking) return;
        if (limitReached) return;
        setChecking(true);
        setResult(null);
        setActiveTab('result');
        try {
            // Record the message first
            const token = localStorage.getItem('token');
            if (token) {
                const recordRes = await fetch(`${API_URL}/api/chatbot/record-message`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (recordRes.status === 403) {
                    setLimitReached(true);
                    setChecking(false);
                    return;
                }
            }

            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const res = await fetch(`${API_URL}/api/grammar-check`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ text: text.trim() })
            });
            const data = await res.json();
            setResult(data);
            if (data.overall_score !== undefined || data.score !== undefined) {
                setHistory(prev => [{ text: text.trim(), ...data, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 10));
            }
            // Refresh status
            fetchChatStatus();
        } catch {
            setResult({ score: 0, overall_score: 0, feedback_bn: 'সার্ভারে সমস্যা হয়েছে।', errors: [], categories: {} });
        }
        setChecking(false);
    };

    const copyCorrection = () => {
        if (result?.corrected) {
            navigator.clipboard.writeText(result.corrected);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const overallScore = result?.overall_score ?? (result?.score ? result.score * 10 : 0);
    const hasCategories = result?.categories && Object.keys(result.categories).length > 0;
    const hasErrors = result?.errors && result.errors.length > 0;

    // Hide chatbot if user is not authenticated or not student
    if (!isAuthenticated || user?.role !== 'student') return null;

    // If chatbot is LOCKED — show FAB but with lock icon, click shows unlock modal
    if (isLocked('chatbot')) {
        return (
            <>
                <button
                    onClick={() => setOpen(true)}
                    data-testid="grammarly-fab-locked"
                    className="fixed bottom-[5.5rem] right-4 sm:bottom-24 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 bg-gradient-to-br from-violet-600 to-indigo-600"
                >
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </button>
                {open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
                        <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                            <div className="h-1.5 w-full bg-gradient-to-r from-violet-600 to-indigo-600" />
                            <div className="p-6">
                                <button onClick={() => setOpen(false)} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                                    <X className="w-3.5 h-3.5 text-gray-500" />
                                </button>
                                <div className="flex justify-center mb-4">
                                    <div className="relative">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-lg">
                                            <Sparkles className="w-8 h-8 text-white" />
                                        </div>
                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center shadow-sm">
                                            <Lock className="w-3 h-3 text-white" />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-center text-lg font-bold text-gray-800 mb-1" style={tb}>Grammar Chatbot Lock আছে</h3>
                                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 my-4 border border-purple-100">
                                    <div className="flex gap-2">
                                        <Sparkles className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />
                                        <p className="text-sm text-gray-700 leading-relaxed" style={tb}>
                                            বড় স্বপ্ন দেখো, বেশি শেখো! এই section তোমার English-কে নতুন উচ্চতায় নিয়ে যাবে, ইনশাল্লাহ।
                                        </p>
                                    </div>
                                </div>
                                <p className="text-center text-xs text-gray-400 mb-3" style={tb}>Unlock করতে যোগাযোগ করুন: <a href="mailto:info@bakkoshilpi.com" className="underline font-semibold text-amber-600">info@bakkoshilpi.com</a></p>
                                <button onClick={() => setOpen(false)}
                                    className="w-full py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold shadow-lg hover:opacity-90 transition-all" style={tb}>
                                    ঠিক আছে, বুঝেছি!
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            {/* FAB Button */}
            <button onClick={() => setOpen(!open)} data-testid="grammarly-fab"
                className={`fixed bottom-[5.5rem] right-4 sm:bottom-24 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${open ? 'bg-slate-700 rotate-45' : 'bg-gradient-to-br from-violet-600 to-indigo-600'}`}>
                {open ? <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> : <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            </button>

            {open && (
                <div ref={panelRef} data-testid="grammarly-panel"
                    className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300
                        bottom-[8.5rem] right-2 left-2 max-h-[65vh]
                        sm:bottom-[7.5rem] sm:right-6 sm:left-auto sm:w-[400px] sm:max-h-[560px]">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-white" />
                            <span className="text-white font-semibold text-sm">Grammar Checker</span>
                            {chatStatus && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${chatStatus.remaining > 3 ? 'bg-white/20 text-white' : 'bg-red-400 text-white'}`}>
                                    {chatStatus.remaining}/{chatStatus.daily_limit}
                                </span>
                            )}
                        </div>
                        <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {/* Limit Reached UI */}
                        {limitReached ? (
                            <div className="text-center py-8 space-y-4">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 flex items-center justify-center">
                                    <Lock className="w-8 h-8 text-red-400" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-red-600 text-sm" style={tb}>আজকের Limit শেষ!</h3>
                                    <p className="text-xs text-slate-500 mt-1" style={tb}>
                                        তোমার আজকের {chatStatus?.daily_limit || 10}টি message ব্যবহার হয়ে গেছে। কাল আবার চেষ্টা করো!
                                    </p>
                                </div>
                                <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
                                    <p className="text-xs text-purple-700" style={tb}>
                                        আরও message limit চাইলে যোগাযোগ করো: info@bakkoshilpi.com
                                    </p>
                                </div>
                            </div>
                        ) : (
                        <>
                        {/* Input Area */}
                        <div>
                            <p className="text-[11px] text-slate-500 mb-1.5 font-bengali">তোমার ইংরেজি বাক্য লেখো, grammar check করব:</p>
                            <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
                                placeholder="Type your English sentence here..."
                                className="w-full px-3 py-2 text-sm border-2 border-slate-200 rounded-xl focus:border-violet-400 outline-none resize-none transition-colors"
                                data-testid="grammarly-input"
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); checkGrammar(); } }} />
                            <div className="flex items-center gap-2 mt-2">
                                <Button size="sm" onClick={checkGrammar} disabled={checking || !text.trim()}
                                    className="bg-violet-600 hover:bg-violet-700 text-xs flex-1 flex items-center justify-center gap-1.5"
                                    data-testid="grammarly-check-btn">
                                    {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    {checking ? 'Analyzing...' : 'Check Grammar'}
                                </Button>
                                {(text || result) && (
                                    <Button size="sm" variant="outline" onClick={() => { setText(''); setResult(null); }}
                                        className="text-xs px-2" data-testid="grammarly-clear">
                                        <RotateCcw className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Results */}
                        {result && (
                            <div className="space-y-3" data-testid="grammarly-result">
                                {/* Score Overview */}
                                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                                    <CircularScore score={overallScore} />
                                    <div className="flex-1 space-y-1">
                                        <p className="text-xs font-bold text-slate-700">
                                            {overallScore >= 80 ? 'Excellent Writing!' :
                                             overallScore >= 60 ? 'Good, some improvements needed' :
                                             overallScore >= 40 ? 'Fair, needs work' : 'Needs significant improvement'}
                                        </p>
                                        {hasCategories && (
                                            <div className="space-y-1.5 mt-2">
                                                {Object.entries(result.categories).map(([key, val]) => (
                                                    <CategoryBar key={key} name={key} data={val} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Tab Buttons */}
                                {(hasErrors || result.corrected) && (
                                    <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                                        <button
                                            onClick={() => setActiveTab('result')}
                                            className={`flex-1 text-[11px] py-1.5 rounded-md font-medium transition-colors ${activeTab === 'result' ? 'bg-white shadow-sm text-violet-700' : 'text-slate-500'}`}
                                            data-testid="tab-result"
                                        >
                                            {hasErrors ? `${result.errors.length} Issues` : 'Result'}
                                        </button>
                                        {result.corrected && result.corrected !== text.trim() && (
                                            <button
                                                onClick={() => setActiveTab('corrected')}
                                                className={`flex-1 text-[11px] py-1.5 rounded-md font-medium transition-colors ${activeTab === 'corrected' ? 'bg-white shadow-sm text-violet-700' : 'text-slate-500'}`}
                                                data-testid="tab-corrected"
                                            >
                                                Corrected
                                            </button>
                                        )}
                                        {result.tips_bn && result.tips_bn.length > 0 && (
                                            <button
                                                onClick={() => setActiveTab('tips')}
                                                className={`flex-1 text-[11px] py-1.5 rounded-md font-medium transition-colors ${activeTab === 'tips' ? 'bg-white shadow-sm text-violet-700' : 'text-slate-500'}`}
                                                data-testid="tab-tips"
                                            >
                                                Tips
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Tab Content: Errors */}
                                {activeTab === 'result' && (
                                    <div className="space-y-2">
                                        {result.feedback_bn && (
                                            <p className="text-[11px] text-slate-600 font-bengali leading-relaxed px-1">{result.feedback_bn}</p>
                                        )}
                                        {hasErrors ? (
                                            result.errors.map((err, i) => <ErrorItem key={i} error={err} index={i} />)
                                        ) : (
                                            <div className="text-center py-4">
                                                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                                <p className="text-xs text-green-600 font-medium font-bengali">কোনো ভুল পাওয়া যায়নি!</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Tab Content: Corrected */}
                                {activeTab === 'corrected' && result.corrected && (
                                    <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[10px] font-semibold text-green-700">Corrected Text:</p>
                                            <button onClick={copyCorrection}
                                                className="flex items-center gap-1 text-[10px] text-green-600 hover:text-green-800 transition-colors"
                                                data-testid="copy-corrected-btn">
                                                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                                {copied ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-green-800 leading-relaxed font-mono">{result.corrected}</p>
                                    </div>
                                )}

                                {/* Tab Content: Tips */}
                                {activeTab === 'tips' && result.tips_bn && (
                                    <div className="space-y-2">
                                        {result.tips_bn.map((tip, i) => (
                                            <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                                                <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                                                <p className="text-[11px] text-amber-800 font-bengali">{tip}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* History */}
                        {history.length > 0 && (
                            <div>
                                <button onClick={() => setShowHistory(!showHistory)}
                                    className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
                                    data-testid="grammarly-history-toggle">
                                    {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    Recent checks ({history.length})
                                </button>
                                {showHistory && (
                                    <div className="space-y-1.5 mt-1.5">
                                        {history.map((h, i) => (
                                            <button key={i} onClick={() => { setText(h.text); setResult(h); }}
                                                className="w-full text-left p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors border border-slate-100"
                                                data-testid={`grammarly-history-${i}`}>
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[11px] text-slate-700 truncate flex-1 mr-2">{h.text}</p>
                                                    <CircularScore score={h.overall_score ?? (h.score ? h.score * 10 : 0)} size={28} />
                                                </div>
                                                <p className="text-[9px] text-slate-400 mt-0.5">{h.time}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
